/**
 * AccountWorkspace — the anchor you return to (Wave 13).
 *
 * One page per department gathering every durable anchor: pinned views
 * (rendered live), open tasks, renewals, competitive position, notes, deals,
 * and the saved-view library. Fluidity lives in the Sales AI canvas; this is
 * the structured place that never disappears.
 *
 * Data: GET /api/workspace — single aggregation round trip; renewals and
 * footprint come from the same chat executors, so workspace numbers can
 * never diverge from chat numbers.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Briefcase, Sparkles, CheckSquare, CalendarClock, FileText, Handshake,
  Bookmark, Pin, ChevronDown, ChevronRight, AlertCircle, Swords, Users,
  CalendarDays, Mail, Copy, Check, Presentation, Play, Network, List, TrendingUp,
  Globe, Building2, Hourglass, UserPlus, PanelRightOpen, PanelRightClose, FileOutput,
  Inbox, Newspaper,
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import { ChatComponentRenderer } from '../ChatWidget/components';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useRole } from '../../hooks/useRole';
import PresentationMode from './PresentationMode';
import OrgChart from './OrgChart';
import ContactInbox, { useContactInboxCount } from './ContactInbox';
import AddContactModal from './AddContactModal';
import ContactDetailModal from './ContactDetailModal';
import ProactiveQuestions from './ProactiveQuestions';
import CommitmentTracker from './CommitmentTracker';
import AccountHealthScore from './AccountHealthScore';
import MeetingHistorySection from './MeetingHistorySection';
import AccountNotesSection from './AccountNotesSection';
import AccountNewsSection from './AccountNewsSection';
import TechMap from './TechMap';
import AIExportModal from './AIExportModal';
import { PurchasingAuthorityCard, PurchasingAuthorityChip } from './PurchasingAuthority';
import StatLine from '../shared/StatLine';
import './AccountWorkspace.css';

function fmtMoney(value) {
  const n = Number(value) || 0;
  if (Math.abs(n) >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function fmtDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysUntil(iso) {
  if (!iso) return null;
  return Math.ceil((new Date(iso) - Date.now()) / 86400000);
}

// Competitor security paper is a displacement window; everything else is
// context. Same Security-category gate the triage renewal rule uses.
function isSecurityRenewal(r) {
  return Number(r.security_value) > 0 || (r.categories || []).includes('Security');
}

// Section headers are chevron + small-caps title + count — no icon.
// One glyph vocabulary per row (audit §5); the icon prop is still
// accepted so call sites don't churn.
function Section({ icon: _icon, title, count, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="aw-section">
      <button className="aw-section-header" onClick={() => setOpen(!open)}>
        {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        <span className="aw-section-title">{title}</span>
        {count !== undefined && <span className="aw-section-count">{count}</span>}
      </button>
      {open && <div className="aw-section-body">{children}</div>}
    </section>
  );
}

function AccountWorkspace({ selectedDepartment = null }) {
  useDocumentTitle('Workspace');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [expandedNote, setExpandedNote] = useState(null);
  const [expandedArtifact, setExpandedArtifact] = useState(null);
  const [expandedContact, setExpandedContact] = useState(null);
  const [expandedDraft, setExpandedDraft] = useState(null);
  const [copiedDraft, setCopiedDraft] = useState(null);
  const [presenting, setPresenting] = useState(null);
  const [contactView, setContactView] = useState(() => {
    // Check URL for view=chart to open org chart directly
    const viewParam = new URLSearchParams(window.location.search).get('view');
    return viewParam === 'chart' ? 'chart' : 'chart'; // default to chart
  });
  const [showAddContact, setShowAddContact] = useState(false);
  const [addContactManager, setAddContactManager] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  // Desktop opens with the rail; phones start closed — there it's a
  // fixed drawer that would otherwise cover the canvas on first paint
  // (Ryan's live pass, 2026-08-12).
  const [sidebarOpen, setSidebarOpen] = useState(
    () => typeof window === 'undefined' || window.innerWidth >= 1024
  );
  const [showAIExport, setShowAIExport] = useState(false);
  const queryClient = useQueryClient();
  const contactInboxCount = useContactInboxCount();
  const { isFeatureEnabled } = useRole();
  const newsEnabled = isFeatureEnabled('news_feed');

  // Sync view from URL param changes
  useEffect(() => {
    const viewParam = searchParams.get('view');
    if (viewParam === 'chart' || viewParam === 'list') {
      setContactView(viewParam);
    }
  }, [searchParams]);

  const deptKey = selectedDepartment?.acronym || selectedDepartment?.official_name || null;

  const [scope, setScope] = useState('account');
  const isGlobal = !deptKey || scope === 'global';

  const { data, isLoading, error } = useQuery({
    queryKey: ['workspace', selectedDepartment?.department_id, deptKey],
    queryFn: () => apiClient.get(
      `/api/workspace?department_id=${selectedDepartment?.department_id || ''}` +
      `&label=${encodeURIComponent(selectedDepartment?.acronym || '')}` +
      `&name=${encodeURIComponent(selectedDepartment?.official_name || '')}`
    ),
    enabled: !!deptKey && !isGlobal,
    staleTime: 60_000,
  });

  const { data: globalData, isLoading: globalLoading, error: globalError } = useQuery({
    queryKey: ['workspace-global'],
    queryFn: () => apiClient.get('/api/workspace/global'),
    enabled: isGlobal,
    staleTime: 60_000,
  });

  const scopeToggle = (
    <div className="aw-scope-toggle">
      <button
        className={!isGlobal ? 'aw-toggle-active' : ''}
        onClick={() => setScope('account')}
        disabled={!deptKey}
        title={deptKey ? 'This account' : 'Select an agency first'}
      >
        <Building2 size={13} /> {deptKey || 'Account'}
      </button>
      <button
        className={isGlobal ? 'aw-toggle-active' : ''}
        onClick={() => setScope('global')}
      >
        <Globe size={13} /> All accounts
      </button>
    </div>
  );

  if (isGlobal) {
    if (globalLoading) {
      return <div className="aw-container"><div className="aw-empty-state"><p>Loading all accounts…</p></div></div>;
    }
    if (globalError) {
      return <div className="aw-container"><div className="aw-error"><AlertCircle size={16} /> {globalError.message}</div></div>;
    }
    const g = globalData || {};
    const gTasks = g.tasks || [];
    const gWaiting = g.waiting || [];
    const gMeetings = g.meetings || [];
    const gOpps = g.opportunities || [];
    const gRenewals = g.renewals || [];
    const gCounts = g.counts || {};

    return (
      <div className="aw-container">
        <header className="aw-header">
          <div className="aw-header-main">
            <h1><Globe size={20} /> All Accounts</h1>
          </div>
          {scopeToggle}
        </header>

        <div className="aw-stats">
          <div className="aw-stat">
            <span className={`aw-stat-value ${gCounts.overdue > 0 ? 'aw-stat-alert' : ''}`}>{gCounts.overdue ?? 0}</span>
            <span className="aw-stat-label">Overdue</span>
          </div>
          <div className="aw-stat">
            <span className="aw-stat-value">{gCounts.open_tasks ?? 0}</span>
            <span className="aw-stat-label">Open tasks</span>
          </div>
          <div className="aw-stat">
            <span className="aw-stat-value">{gCounts.waiting ?? 0}</span>
            <span className="aw-stat-label">Waiting on others</span>
          </div>
          <div className="aw-stat">
            <span className="aw-stat-value">{fmtMoney(g.pipeline_total)}</span>
            <span className="aw-stat-label">Pipeline ({gCounts.opportunities ?? 0})</span>
          </div>
        </div>

        <div className="aw-columns">
          <div className="aw-col">
            <Section icon={CheckSquare} title="Action queue" count={gTasks.length}>
              {gTasks.length === 0 && <p className="aw-none">Nothing open — clean slate.</p>}
              {gTasks.map((t) => {
                const d = daysUntil(t.due_date);
                return (
                  <div key={t.id} className="aw-task">
                    <span className="aw-task-title">{t.title}</span>
                    <span className="aw-task-meta">
                      {t.priority && <span className={`aw-chip aw-prio-${t.priority}`}>{t.priority}</span>}
                      {t.due_date && (
                        <span className={`aw-due ${d < 0 ? 'aw-due-overdue' : d <= 2 ? 'aw-due-soon' : ''}`}>
                          {d < 0 ? `${-d}d overdue` : d === 0 ? 'today' : `in ${d}d`}
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </Section>

            <Section icon={Hourglass} title="Waiting on others" count={gWaiting.length}>
              {gWaiting.length === 0 && <p className="aw-none">Nothing blocked on customers right now.</p>}
              {gWaiting.map((t) => (
                <div key={t.id} className="aw-task">
                  <span className="aw-task-title">{t.title}</span>
                  <span className="aw-task-meta">
                    {t.waiting_for_who && <span className="aw-chip">{t.waiting_for_who}</span>}
                  </span>
                </div>
              ))}
            </Section>
          </div>

          <div className="aw-col">
            <Section icon={CalendarDays} title="Upcoming meetings" count={gMeetings.length}>
              {gMeetings.length === 0 && <p className="aw-none">No meetings scheduled.</p>}
              {gMeetings.map((m) => (
                <div key={m.meeting_id} className="aw-meeting">
                  <div className="aw-meeting-head">
                    <span className="aw-meeting-title">{m.title}</span>
                    <span className="aw-meeting-meta">
                      {m.department_label && <span className="aw-chip">{m.department_label}</span>}
                      {m.meeting_at && <span className="aw-time">{fmtDate(m.meeting_at)}</span>}
                    </span>
                  </div>
                </div>
              ))}
            </Section>

            <Section icon={TrendingUp} title="Pipeline" count={gOpps.length}>
              {gOpps.length === 0 && <p className="aw-none">No open opportunities yet.</p>}
              {gOpps.map((o) => (
                <div key={o.id} className="aw-opp">
                  <div className="aw-opp-head">
                    <span className="aw-opp-name">{o.name}</span>
                    <span className="aw-opp-value">{o.value ? fmtMoney(o.value) : '—'}</span>
                  </div>
                  <span className="aw-opp-meta">
                    <span className="aw-chip aw-opp-stage">{o.stage}</span>
                    {o.source === 'ai-derived' && <span className="aw-chip aw-opp-derived">auto-detected</span>}
                    {o.department_label && <span className="aw-time">{o.department_label}</span>}
                  </span>
                </div>
              ))}
            </Section>

            <Section icon={CalendarClock} title="Renewals — next 6 months" count={g.renewals_summary?.vendors_with_renewals}>
              {gRenewals.length === 0 && <p className="aw-none">No renewals in the window.</p>}
              {gRenewals.map((r, i) => {
                const d = daysUntil(r.first_expiry);
                return (
                  <div key={i} className="aw-renewal">
                    <div className="aw-renewal-head">
                      <span className="aw-renewal-vendor">{r.vendor}</span>
                      {isSecurityRenewal(r) && <span className="aw-chip aw-chip-security">security</span>}
                    </div>
                    <span className="aw-renewal-meta">
                      <span className="aw-renewal-value">{fmtMoney(r.total_value)}</span>
                      <span className={`aw-due ${d !== null && d <= 30 ? 'aw-due-soon' : ''}`}>{fmtDate(r.first_expiry)}</span>
                    </span>
                  </div>
                );
              })}
            </Section>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <div className="aw-container"><div className="aw-empty-state"><p>Loading workspace…</p></div></div>;
  }
  if (error) {
    return <div className="aw-container"><div className="aw-error"><AlertCircle size={16} /> {error.message}</div></div>;
  }

  const { notes = [], deals = [], artifacts = [], tasks = [], renewals = {}, footprint = {}, contacts = [], meetings = [], drafts = [], presentations = [], opportunities = [], pipeline_total = 0, counts = {}, account_id = null, purchasing_authority: purchasingAuthority = null } = data || {};
  const upcomingMeetings = meetings.filter((m) => m.status === 'planned');
  const pastMeetings = meetings.filter((m) => m.status !== 'planned');
  const pinned = artifacts.filter((a) => a.pinned);
  const unpinned = artifacts.filter((a) => !a.pinned);
  // Security paper first (displacement plays), then soonest expiry — the
  // renewal calendar is the account-page home for what the old gap rule
  // used to spam into Next Actions.
  const renewalRows = [...(renewals.renewals || [])]
    .sort((a, b) => {
      const sec = Number(isSecurityRenewal(b)) - Number(isSecurityRenewal(a));
      if (sec !== 0) return sec;
      return new Date(a.first_expiry || '9999-12-31') - new Date(b.first_expiry || '9999-12-31');
    })
    .slice(0, 6);
  const fpSummary = footprint.summary || {};

  const copyDraft = async (d) => {
    try {
      await navigator.clipboard.writeText(d.content || '');
      setCopiedDraft(d.id);
      setTimeout(() => setCopiedDraft(null), 2000);
    } catch (e) { /* clipboard unavailable */ }
  };

  // Org-chart-first layout: org chart takes most of the screen
  return (
    <div className="aw-fullscreen">
      {/* ONE full-width toolbar (audit slice 3). The account name already
          lives in the topnav selector and the breadcrumb — the old title
          row repeated it a third time. View switcher, stats, scope and
          actions share this single row; content starts right under it. */}
      <header className="aw-topbar">
        <div className="aw-view-toggle">
          <button
            className={contactView === 'chart' ? 'aw-toggle-active' : ''}
            onClick={() => setContactView('chart')}
          >
            <Network size={14} /> Org chart
          </button>
          <button
            className={contactView === 'list' ? 'aw-toggle-active' : ''}
            onClick={() => setContactView('list')}
          >
            <List size={14} /> List view
          </button>
          <button
            className={contactView === 'techmap' ? 'aw-toggle-active' : ''}
            onClick={() => setContactView('techmap')}
          >
            <Globe size={14} /> Tech map
          </button>
        </div>
        <div className="aw-topbar-stats">
          <span className="aw-mini-stat">
            <strong>{contacts.length}</strong> contacts
          </span>
          <span className="aw-mini-stat">
            <strong>{counts.open_tasks ?? 0}</strong> tasks
          </span>
          <span className="aw-mini-stat">
            <strong>{fmtMoney(pipeline_total)}</strong> pipeline
          </span>
          <span className="aw-mini-stat">
            <strong>{fmtMoney(renewals.summary?.total_value)}</strong> renewing
          </span>
          <PurchasingAuthorityChip purchasingAuthority={purchasingAuthority} />
        </div>
        <div className="aw-topbar-right">
          {scopeToggle}
          <button
            className="aw-add-contact-btn"
            onClick={() => {
              setAddContactManager(null);
              setShowAddContact(true);
            }}
          >
            <UserPlus size={14} /> Add contact
          </button>
          <button
            className="aw-export-ai"
            onClick={() => setShowAIExport(true)}
            title="Export for AI Analysis (NotebookLM)"
          >
            <FileOutput size={14} /> <span>Export for AI</span>
          </button>
          <button className="aw-open-ai" onClick={() => navigate('/sales-ai')}>
            <Sparkles size={14} /> <span>Sales AI</span>
          </button>
          <button
            className="aw-toggle-sidebar"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            title={sidebarOpen ? 'Hide details' : 'Show details'}
          >
            {sidebarOpen ? <PanelRightClose size={18} /> : <PanelRightOpen size={18} />}
          </button>
        </div>
      </header>

      {/* Main content area: org chart + sidebar */}
      <div className="aw-main-split">
        {/* PRIMARY: Org chart area */}
        <div className="aw-org-primary">
          {/* Org chart display area */}
          <div className="aw-org-canvas">
            {contactView === 'techmap' && (
              <div className="aw-techmap-canvas">
                {/* Tech Map — the account's technology estate as a draggable
                    canvas, seeded from the caller's visible notes. Owner-
                    private (mirrors note visibility); read-only twin in GAS. */}
                <TechMap
                  account={selectedDepartment?.acronym || String(selectedDepartment?.department_id || '')}
                />
              </div>
            )}
            {contactView === 'chart' && (
              // Always mounted (GAS parity): an empty account still shows
              // the discovery toolbar + ghost suggestions; the wrapper owns
              // the empty-state hero.
              <OrgChart
                contacts={contacts}
                accountId={account_id}
                departmentId={selectedDepartment?.department_id}
                departmentLabel={selectedDepartment?.acronym || selectedDepartment?.official_name}
                onChanged={() => queryClient.invalidateQueries({ queryKey: ['workspace'] })}
                onAddContact={(manager) => {
                  setAddContactManager(manager);
                  setShowAddContact(true);
                }}
                onContactClick={(contact) => setSelectedContact(contact)}
                fullscreen
              />
            )}
            {contactView === 'list' && (
              <div className="aw-list-view">
                {contacts.length === 0 && (
                  <p className="aw-none">No contacts yet — mention people in Sales AI ("Robert Mayorga is the CISO") and they'll be recorded here.</p>
                )}
                {contacts.map((c) => {
                  const fullName = `${c.first_name} ${c.last_name || ''}`.trim();
                  return (
                    <div
                      key={c.id}
                      className="aw-contact aw-contact-clickable"
                      onClick={() => setSelectedContact(c)}
                    >
                      <div className="aw-contact-head">
                        <span className="aw-contact-name">{fullName}</span>
                        <span className="aw-contact-meta">
                          {c.influence_level && c.influence_level !== 'unknown' && (
                            <span className={`aw-chip aw-infl-${c.influence_level}`}>
                              {c.influence_level.replace('_', ' ')}
                            </span>
                          )}
                          {c.relationship_status && <span className="aw-chip">{c.relationship_status}</span>}
                        </span>
                      </div>
                      <span className="aw-contact-title">
                        {c.title || 'Title unknown'}
                        {c.reports_to && <span className="aw-contact-reports"> · reports to {c.reports_to}</span>}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* SECONDARY: Collapsible details sidebar */}
        <aside className={`aw-details-sidebar ${sidebarOpen ? 'aw-sidebar-open' : 'aw-sidebar-closed'}`}>
          <div className="aw-sidebar-scroll">
            {/* How big a deal this account can sign itself. It sits above the
                intelligence widgets because it frames them: a $2M renewal at a
                Tier 1 account is a DGS conversation, not an account one. */}
            <PurchasingAuthorityCard purchasingAuthority={purchasingAuthority} />

            {/* Intelligence widgets */}
            {account_id && (
              <div className="aw-sidebar-intelligence">
                <AccountHealthScore accountId={account_id} collapsed />
                <ProactiveQuestions accountId={account_id} accountName={deptKey} collapsed />
                <CommitmentTracker accountId={account_id} collapsed />
              </div>
            )}

            {/* Quick stats — inline, not boxed tiles (audit §5) */}
            <StatLine
              stats={[
                { value: (renewals.renewals || []).filter(isSecurityRenewal).length, label: 'security renewals' },
                { value: fmtMoney(fpSummary.competitor_spend), label: 'competitor' },
              ]}
            />

            {/* Renewals lead the collapsible stack: expiring paper is the
                account's clock — competitor security renewals are the
                displacement windows the triage gap rule points at. */}
            <Section icon={CalendarClock} title="Renewals — next 18 months" count={renewals.summary?.vendors_with_renewals} defaultOpen={renewalRows.length > 0}>
              {renewalRows.length === 0 && <p className="aw-none">No upcoming renewals in the window.</p>}
              {renewalRows.map((r, i) => {
                const d = daysUntil(r.first_expiry);
                return (
                  <div key={i} className="aw-renewal aw-renewal-rich">
                    <div className="aw-renewal-head">
                      <span className="aw-renewal-vendor">{r.vendor}</span>
                      {isSecurityRenewal(r) && <span className="aw-chip aw-chip-security">security</span>}
                    </div>
                    {(r.components || []).length > 0 && (
                      <span className="aw-renewal-products">{r.components.slice(0, 2).join(' · ')}</span>
                    )}
                    <span className="aw-renewal-meta">
                      <span className="aw-renewal-value">{fmtMoney(r.total_value)}</span>
                      <span className={`aw-due ${d !== null && d <= 30 ? 'aw-due-soon' : ''}`}>
                        {fmtDate(r.first_expiry)}{d !== null && d >= 0 ? ` · ${d}d` : ''}
                      </span>
                    </span>
                  </div>
                );
              })}
            </Section>

            {/* News tagged to this agency — press + TekStak signals (staff
                changes, BCPs, solicitations); people from this org chart
                who appeared in it are chips that open the contact. */}
            {newsEnabled && (
            <Section icon={Newspaper} title="News" defaultOpen>
              <AccountNewsSection
                account={selectedDepartment?.acronym || String(selectedDepartment?.department_id || '')}
                onContactClick={(c) => {
                  const hit = contacts.find((x) => x.id === c.contact_id);
                  if (hit) setSelectedContact(hit);
                }}
              />
            </Section>
            )}

            {/* Collapsed sections */}
            <Section icon={Inbox} title="Contact Inbox (all accounts)" count={contactInboxCount} defaultOpen={contactInboxCount > 0}>
              <ContactInbox />
            </Section>

            <Section icon={TrendingUp} title="Opportunities" count={counts.opportunities} defaultOpen={false}>
              {opportunities.length === 0 && <p className="aw-none">No opportunities yet.</p>}
              {opportunities.map((o) => (
                <div key={o.id} className="aw-opp">
                  <div className="aw-opp-head">
                    <span className="aw-opp-name">{o.name}</span>
                    <span className="aw-opp-value">{o.value ? fmtMoney(o.value) : '—'}</span>
                  </div>
                  <span className="aw-opp-meta">
                    <span className="aw-chip aw-opp-stage">{o.stage}</span>
                  </span>
                </div>
              ))}
            </Section>

            <Section icon={CalendarDays} title="Meetings" count={counts.meetings} defaultOpen={false}>
              {meetings.length === 0 && <p className="aw-none">No meetings scheduled.</p>}
              {upcomingMeetings.slice(0, 3).map((m) => (
                <div key={m.meeting_id} className="aw-meeting">
                  <div className="aw-meeting-head">
                    <span className="aw-meeting-title">{m.title}</span>
                    <span className="aw-meeting-meta">
                      {m.meeting_at && <span className="aw-time">{fmtDate(m.meeting_at)}</span>}
                    </span>
                  </div>
                </div>
              ))}
            </Section>

            {/* Customer Messaging Notes — shared with the GAS Notes tab.
                Keyed on the department (not the CRM account UUID) so capture
                works before an accounts row exists for this agency. */}
            {(selectedDepartment?.acronym || selectedDepartment?.department_id) && (
              <AccountNotesSection
                account={selectedDepartment?.acronym || String(selectedDepartment?.department_id)}
              />
            )}

            {/* Plaud meeting recordings */}
            {account_id && (
              <MeetingHistorySection
                accountId={account_id}
                accountName={deptKey}
              />
            )}

            <Section icon={CheckSquare} title="Tasks" count={tasks.length} defaultOpen={false}>
              {tasks.length === 0 && <p className="aw-none">No open tasks.</p>}
              {tasks.slice(0, 5).map((t) => {
                const d = daysUntil(t.due_date);
                return (
                  <div key={t.id} className="aw-task">
                    <span className="aw-task-title">{t.title}</span>
                    <span className="aw-task-meta">
                      {t.due_date && (
                        <span className={`aw-due ${d < 0 ? 'aw-due-overdue' : d <= 2 ? 'aw-due-soon' : ''}`}>
                          {d < 0 ? `${-d}d overdue` : d === 0 ? 'today' : `in ${d}d`}
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </Section>

            <Section icon={FileText} title="Notes" count={counts.notes} defaultOpen={false}>
              {notes.length === 0 && <p className="aw-none">No notes yet.</p>}
              {notes.slice(0, 5).map((n) => (
                <div
                  key={n.note_id}
                  className={`aw-note ${expandedNote === n.note_id ? 'aw-note-open' : ''}`}
                  onClick={() => setExpandedNote(expandedNote === n.note_id ? null : n.note_id)}
                >
                  <div className="aw-note-head">
                    <span className="aw-note-title">{n.title}</span>
                    <span className="aw-time">{fmtDate(n.created_at)}</span>
                  </div>
                  {expandedNote === n.note_id && <p className="aw-note-content">{n.content}</p>}
                </div>
              ))}
            </Section>

            <Section icon={Swords} title="Competitive" defaultOpen={false}>
              <div className="aw-fp">
                <div className="aw-fp-row">
                  <span>Our footprint</span>
                  <strong>{fmtMoney(fpSummary.our_spend)}</strong>
                </div>
                <div className="aw-fp-row">
                  <span>Competitors</span>
                  <strong>{fmtMoney(fpSummary.competitor_spend)}</strong>
                </div>
              </div>
            </Section>
          </div>
        </aside>
        {/* Phone: the rail is a fixed drawer there — backdrop closes it
            on outside tap (display: none ≥768px). Must stay the aside's
            NEXT sibling for the ~ selector in the CSS. */}
        {sidebarOpen && (
          <div
            className="aw-sidebar-backdrop"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}
      </div>

      {presenting && (
        <PresentationMode presentation={presenting} onClose={() => setPresenting(null)} />
      )}

      {showAddContact && (
        <AddContactModal
          onClose={() => {
            setShowAddContact(false);
            setAddContactManager(null);
          }}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: ['workspace'] })}
          contacts={contacts}
          accountId={account_id}
          departmentId={selectedDepartment?.department_id}
          departmentLabel={selectedDepartment?.acronym || selectedDepartment?.official_name}
          preselectedManager={addContactManager}
        />
      )}

      {selectedContact && (
        <ContactDetailModal
          contact={selectedContact}
          onClose={() => setSelectedContact(null)}
          onUpdate={() => queryClient.invalidateQueries({ queryKey: ['workspace'] })}
          allContacts={contacts}
        />
      )}

      {showAIExport && (
        <AIExportModal
          departmentId={selectedDepartment?.department_id}
          departmentName={selectedDepartment?.official_name}
          acronym={selectedDepartment?.acronym}
          onClose={() => setShowAIExport(false)}
        />
      )}
    </div>
  );
}

export default AccountWorkspace;
